import "./application.css";
import { createInertiaApp } from "@inertiajs/react";
import { Theme, Box, Text, Flex } from "@radix-ui/themes";
import { createRoot } from "react-dom/client";
import { Component, type ReactNode } from "react";

class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("React Error Boundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box p="4">
          <Flex direction="column" gap="2">
            <Text size="4" weight="bold" color="red">
              Something went wrong
            </Text>
            <Text size="2" color="gray">
              The application encountered an error. Please check the console for details.
            </Text>
            {this.state.error && (
              <Box
                p="2"
                style={{
                  background: "var(--gray-3)",
                  borderRadius: "4px",
                  fontFamily: "monospace",
                  fontSize: "12px",
                }}
              >
                <Text size="1">{this.state.error.message}</Text>
              </Box>
            )}
          </Flex>
        </Box>
      );
    }

    return this.props.children;
  }
}

createInertiaApp({
  resolve: (name) => {
    const pages = import.meta.glob(["../pages/**/*.tsx", "!../pages/**/*.test.tsx"], {
      eager: true,
    });
    const page = pages[`../pages/${name}.tsx`];

    if (!page) {
      throw new Error(`Inertia page not found: ${name}`);
    }

    return page;
  },
  setup({ el, App, props }) {
    createRoot(el).render(
      <Theme accentColor="indigo" grayColor="slate" radius="medium" scaling="100%">
        <ErrorBoundary>
          <App {...props} />
        </ErrorBoundary>
      </Theme>,
    );
  },
});
